import React, {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    ReactNode,
} from 'react'
import { isDesktop } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
// For the controller objects below: they live at module level, so the hook's `t`
// is not in scope there. Inside components the local `const { t }` shadows this.
import { t } from 'i18next'
import {
    Layout,
    Model,
    TabNode,
    Actions,
    DockLocation,
    ITabRenderValues,
    BorderNode,
    Action,
} from 'flexlayout-react'
import { useSignalEffect, useSignals } from '@preact/signals-react/runtime'
import { Container, Header, Message, Modal, Nav, Navbar, Panel, PanelGroup } from 'rsuite'
import CogIcon from '@rsuite/icons/legacy/Cog'
import { headerHeight, defaultSidebarWidth } from '@client/config/constants'
import { SettingsIcon, SubscriptionIcon, UserIcon, HelpIcon } from '@/client/config'
import ProjectList from '@client/views/projects/ProjectList'
import { Button } from '@client/components/Controls'
import LeftNavigation from './LeftNavigation'
import { findMenu } from '@client/config/views'
import tabStore, { ETabSet, TTabInput, TTabItem, TTabItemController } from '@client/stores/tabsStore'
import { LogoIcon } from '../SvgLoader'
import AuthProvider, { useAuth } from '@client/components/AuthProvider'
import Help from '../../views/help'
import Settings from '../../views/settings'
import { user as authUser } from '@/client/stores/authStore'
import project from '@/client/stores/projectStore'
import ContentLogo from './ContentLogo'
import TabContent from './TabContent'
import { sidebarVisible } from '@/client/stores/sidebarStore'
import EditUserProfile from '@/client/views/users/EditUserProfile'
import { Capabilities, hasPermission, RoleType } from '@/shared'
import { ESubscriptionStatus } from '@/client/models/subscription'
import { hasRole } from '@/shared/roles'
import { ContextMenu, ContextMenuItem, TContextMenuCurrentItem } from '@/client/components/ContextMenu'
import tabsStore from '@client/stores/tabsStore'
import { getSocket } from '@client/socket/socket.ts'
import { toastError } from '@client/utils/toastMessage.tsx'
import useConversionsQuestion from '@client/views/converter/useConversionsQuestion'
import useConversionsOnReload from '@client/views/converter/useConversionsOnReload'
import { cancelOwnConversions, findActiveConversionsOf } from '@client/views/converter/activeConversions'
import { useConnection } from '../ConnectionProvider'
import { nl2br } from '@/shared'
import UserModel from '@/client/models/user'
import { dirtyRevision } from '@client/utils/useTabDirty'

const STORAGE_KEY_LAYOUT = 'saved-layout'
const STORAGE_KEY_TABS = 'saved-tabs'
const STORAGE_KEY_SIDEBAR_WIDTH = 'sidebar-width'

/** The width of the sidebar a person set, in px; null for the default. */
const readSidebarWidth = (): number | null => {
    try {
        const value = Number(localStorage.getItem(STORAGE_KEY_SIDEBAR_WIDTH))
        return Number.isFinite(value) && value > 0 ? value : null
    } catch {
        return null
    }
}

const storeSidebarWidth = (px: number) => {
    try {
        localStorage.setItem(STORAGE_KEY_SIDEBAR_WIDTH, String(Math.round(px)))
    } catch {
        // storage blocked: the width lasts until the page is left
    }
}

const svgNavHeaderStyle = {
    width: '5em',
    maxWidth: '100%',
    fill: 'var(--special-color-1)',
    alignSelf: 'center',
    justifySelf: 'center',
    maxHeight: headerHeight - 10,
} as React.CSSProperties
const messageWrapperStyle = {
    position: 'fixed',
    top: '1em',
    left: '1em',
    right: '1em',
    zIndex: 1000000,
} as React.CSSProperties
const connectionPanelStyle = {
    background: 'var(--special-color-3)',
    textAlign: 'center',
} as React.CSSProperties

const userModel = new UserModel()

// --- context menu ---

const OptimizedMessage = () => {
    if (isDesktop) return null

    const { t } = useTranslation()

    return (
        <div style={messageWrapperStyle}>
            <Message full type="error" closable>
                {nl2br(t('This program is not responsive.\nIt is optimized only for desktop monitors.'), true)}
            </Message>
        </div>
    )
}

const NoConnectionMessage = () => {
    const { isOnline, isServerReachable } = useConnection()
    const { t } = useTranslation()

    if (isOnline && isServerReachable) return null

    return (
        <div className="overlay overlay-1060">
            <Panel className="messagebox" style={connectionPanelStyle}>
                <PanelGroup className="messagebox-content">
                    {nl2br(t('Connection to the server lost.\nCheck your Internet connection.'), true)}
                </PanelGroup>
            </Panel>
        </div>
    )
}

// --- context menu ---

export type TContextMenuItem = {
    onClick?: (event: React.MouseEvent, currentElement: TContextMenuCurrentItem) => void
    label?: string
    separator?: boolean
    /** an option that is on or off: the menu shows its state */
    checked?: boolean
}

const initialContextMenu = {
    visible: false,
    x: 0,
    y: 0,
    items: [] as TContextMenuItem[],
    currentElement: null as TContextMenuCurrentItem | null,
}

// --- context ---

type TLayoutContext = {
    openProject: boolean
    setOpenProject: Dispatch<SetStateAction<boolean>>
    NoProjectSelected: () => ReactNode
    model: Model | null
    addTab: (tab: TTabInput) => void
    cmd: (action: string, data?: any, defaultValue?: any) => any
    onContextMenu: (
        event: React.MouseEvent,
        contextMenuItems: TContextMenuItem[],
        currentElement: TContextMenuCurrentItem,
        at?: { x: number; y: number }
    ) => void
    openTabToRight: (tab: TTabInput) => void
    closeTab: (tabId: string) => void
    removeTabCopies: (sourceTabId: string) => void
    removeOtherTabs: (sourceTabId: string) => void
}

const LayoutContext = createContext<TLayoutContext>({
    openProject: false,
    setOpenProject: () => { },
    NoProjectSelected: () => { },
    model: null,
    addTab: () => { },
    cmd: () => { },
    onContextMenu: () => { },
    openTabToRight: () => { },
    closeTab: () => { },
    removeTabCopies: () => { },
    removeOtherTabs: () => { },
})

export function useLayout() {
    return useContext(LayoutContext)
}

/**
 * Layout structure
 */
const layoutStructure = {
    global: {
        tabEnableClose: true,
        tabEnableRename: false,
        // Docking at an edge of the layout never worked: setOnAllowDrop refuses
        // every drop on the outer row. Its markers still showed, the left one
        // beside the sidebar (operator, 15.10.2026: "Das ist unschön.").
        enableEdgeDock: false,
    },
    layout: {
        type: 'row',
        id: 'layout',
        weight: 100,
        children: [
            {
                type: 'tabset',
                id: 'sidebar',
                weight: defaultSidebarWidth,
                enableDrag: false,
                enableDrop: false,
                enableClose: false,
                enableMaximize: false,
                enableTabStrip: false,
                enableDeleteWhenEmpty: false,
                children: [],
            },
            {
                type: 'tabset',
                id: 'main-tabs',
                weight: 80,
                enableClose: false,
                enableMaximize: false,
                enableDeleteWhenEmpty: false,
                children: [],
            },
        ],
    },
}

/**
 * Default controller
 * Used for every tab opend by the sidebar
 */
const controller: TTabItemController = {
    defaultRow(params?: any) {
        return {}
    },

    removeTab(params?: any) {
        return true
    },

    showBreadCrumb(params?: any) {
        return true
    },

    useProjectInBreadCrumb(params?: any) {
        return true
    },

    getBreadCrumb(params?: any) {
        const { tab } = params
        const breadCrumb: string[] = []

        let node = tab?.store?.data || { label: '???' }
        breadCrumb.unshift(node.label)
        while (node?.parent) {
            node = node.parent
            breadCrumb.unshift(node.label)
        }
        return breadCrumb
    },

    ignoreProjectExists(params?: any) {
        return params
    },
}

/**
 * Help controller
 */
const helpController = {
    defaultRow(params?: any) {
        return {}
    },

    getTabContent(params: any) {
        return <Help />
    },

    useProjectInBreadCrumb(params: any) {
        return false
    },

    showBreadCrumb(params: any) {
        return false
    },
    ignoreProjectExists(params: any) {
        return true
    },
}

/**
 * Settings controller
 */
const settingsController = {
    defaultRow(params?: any) {
        return {}
    },

    getTabContent(params: any) {
        return <Settings />
    },

    useProjectInBreadCrumb(params: any) {
        return false
    },

    showBreadCrumb(params: any) {
        return false
    },
    ignoreProjectExists(params: any) {
        return true
    },
}

/**
 * User Profile controller
 */
const userProfileController = {
    defaultRow(params?: any) {
        return {}
    },

    getTabContent(params: any) {
        return <EditUserProfile />
    },

    useProjectInBreadCrumb(params: any) {
        return false
    },

    showBreadCrumb(params: any) {
        return false
    },
    ignoreProjectExists(params: any) {
        return true
    },
}

/**
 * User Subscription controller
 */
const userSubscriptionController = {
    defaultRow(params?: any) {
        const isUserSubscription = params?.isUserSubscription
        const user = authUser.value

        const row = {
            _id: '',
            userId: '',
            user: null,
            startedAt: '',
            recurringDate: '',
            status: ESubscriptionStatus.Active,
        }

        if (isUserSubscription && user?._id) {
            row.user = user
            row.userId = user._id
        }

        return row
    },

    getTabContent(params: any) {
        const { tab } = params
        return <p>{t('Not implemented yet.')}</p>
    },

    useProjectInBreadCrumb(params: any) {
        return false
    },

    showBreadCrumb(params: any) {
        return false
    },
    ignoreProjectExists(params: any) {
        return true
    },
}

/**
 * Returns the specified content for the given tab
 */
const useFactory = () =>
    useCallback((node: TabNode) => {
        const component = node.getComponent()
        const config = node.getConfig()

        // 1. search in navigation
        const view = findMenu(component as string)
        if (view) return view?.sidebarContent

        // 2. search in config.controller
        if (config?.controller) return <TabContent config={config} />

        // 3. not found
        return <div key={node.getId()}>Unknown component: {component}</div>
    }, [])

/**
 * Create the tab name label
 * @param tab
 * @returns the the label
 */
const getTabName = (tab: TTabItem): string => {
    const allTabs = tabStore.getTabs()
    const sameDataTabs = allTabs.filter((t) => t.dataId === tab.dataId)

    // Find how many tabs with the same dataId were opened before this one
    const index = sameDataTabs.findIndex((t) => t.id === tab.id)

    if (index === -1) return tab.name // fallback
    if (index === 0) return tab.name // first tab: no suffix
    return `${tab.name} (${index + 1})` // duplicates: add (2), (3), ...
}
function getParent(node: any) {
    return typeof node?.getParent === 'function' ? node.getParent() : null
}

function getId(node: any) {
    return typeof node?.getId === 'function' ? node.getId() : undefined
}
function getType(n: any) {
    return typeof n?.getType === 'function' ? n.getType() : undefined
}

function isInsideSidebar(node: any): boolean {
    let cur = node
    while (cur) {
        if (getId(cur) === ETabSet.SIDEBAR) return true
        cur = getParent(cur)
    }
    return false
}
/**
 * The sidebar in the outer row. Weights are relative: flexlayout draws them as
 * flex-grow, so the sidebar gets its weight against the sum of the others, of
 * the width the splitters leave. Splitting the tab area and closing the split
 * again halved the others each time, and the sidebar grew (15.10.2026).
 */
const sidebarGeometry = (model: Model) => {
    const row = model.getRootRow()
    const children = row.getChildren() as any[]
    const sidebar = children.find((child) => child.getId() === ETabSet.SIDEBAR)
    const rowWidth = document.querySelector('.flexlayout__layout')?.getBoundingClientRect().width ?? 0
    if (!sidebar || !rowWidth) return null
    const available = rowWidth - (model.getSplitterSize() ?? 0) * (children.length - 1)
    const others = children.reduce((sum, child) => (child === sidebar ? sum : sum + child.getWeight()), 0)
    const weight = sidebar.getWeight()
    const width = weight + others > 0 ? (weight / (weight + others)) * available : 0
    return { available, others, weight, width }
}

/** The weight that makes the sidebar `px` wide beside the panes it has now. */
const sidebarWeightFor = (geometry: NonNullable<ReturnType<typeof sidebarGeometry>>, px: number) =>
    geometry.available > px && geometry.others > 0 ? (px * geometry.others) / (geometry.available - px) : geometry.weight

/**
 * Generates the layout model
 * @returns model of the layout
 */
const getInitialModel = (): Model => {
    const layout = { ...layoutStructure }
    // A layout saved by an older version is no longer read: one without the
    // main tab set failed on the first tab (15.10.2026). Only the width of the
    // sidebar is kept, under a key of its own.
    try {
        localStorage.removeItem(STORAGE_KEY_LAYOUT)
    } catch {
        // storage blocked: nothing to clear
    }

    const model = Model.fromJson(layout)

    model.setOnAllowDrop((dragNode, dropInfo) => {
        const { location, node, index } = dropInfo

        // Never allow border drops
        if (node instanceof BorderNode) return false

        // Never allow drops inside the sidebar or its subtree
        if (isInsideSidebar(node)) return false

        // Never allow drops to the left of the sidebar
        if (getId(node) === 'layout' && getType(node) === 'row') return false

        return true
    })

    return model
}

// --- top navigation ---

const TopNavbar = () => {
    const { t } = useTranslation()
    const { handleLogout } = useAuth()
    const { addTab, setOpenProject } = useLayout()
    const conversionsQuestion = useConversionsQuestion()
    const userId = String(authUser.value?._id || '')
    useConversionsOnReload(userId, (message) => conversionsQuestion.ask(message))

    // One's own conversions run on after signing out, unless asked otherwise (F3).
    const onSignOut = async () => {
        const own = await findActiveConversionsOf(userId)
        if (own.length) {
            const answer = await conversionsQuestion.ask(
                t('Your conversions are still waiting or running. Cancel them or let them run?'),
                true
            )
            if (answer === 'back') return
            if (answer === 'cancel') for (const reason of await cancelOwnConversions(own)) toastError(reason)
        }
        handleLogout()
    }

    const [userName, setUserName] = useState<string>('')
    //const userName = auth.user?.value.userName
    //const userName = auth.user?.value.firstName
    useSignalEffect(() => {
        if (authUser.value) setUserName(authUser?.value.firstName)
    })

    const onOpenProjectDialog = () => {
        setOpenProject(true)
    }

    const onCloseProject = () => {
        project.clearProject()

        const { openProject, settings } = authUser?.value.settings
        userModel.updateSettings(authUser?.value._id, settings || {})
    }

    const onOpenHelp = () => {
        addTab({
            dataId: 'help',
            name: t('Help'),
            component: 'help',
            controller: helpController,
            icon: <HelpIcon />,
            store: {},
            context: 'main-tabs',
        })
    }
    const onOpenSettings = () => {
        addTab({
            dataId: 'settings',
            name: t('Settings'),
            component: 'settings',
            controller: settingsController,
            icon: <SettingsIcon />,
            store: {},
            context: 'main-tabs',
        })
    }
    const onOpenUserProfile = () => {
        addTab({
            dataId: 'userProfile',
            name: t('My Profile'),
            component: 'editUserProfile',
            controller: userProfileController,
            icon: <UserIcon />,
            store: {},
            context: 'main-tabs',
        })
    }
    const onOpenUserSubscription = () => {
        const subscription = { ...(authUser.value.subscription || null), user: authUser.value }
        delete subscription.user.subscription

        addTab({
            dataId: 'my-subscription',
            name: t('My Subscription'),
            component: 'subscription',
            controller: userSubscriptionController,
            icon: <SubscriptionIcon />,
            store: {
                id: subscription?._id,
                initialData: subscription,
                formData: subscription,
                isUserSubscription: true,
            },
        })
    }

    return (
        <Header className="top-nav-header">
            <Navbar appearance="subtle" className="nav-toggle top-navbar">
                <Nav className="top-left-nav">
                    <LogoIcon style={svgNavHeaderStyle} link="https://msbc-converter.com" openNewWindow={true} />

                    <Nav.Menu noCaret={false} placement="bottomStart" trigger="click" title={t('Projects')}>
                        <Nav.Item onClick={onOpenProjectDialog}>{t('Open project')}</Nav.Item>
                        <Nav.Item onClick={onCloseProject} disabled={!project.currentProject.value?.name}>{t('Close project')}</Nav.Item>
                    </Nav.Menu>
                </Nav>

                <Nav className="top-center-nav">
                    <span>{project.currentProject.value?.name || t('No project selected.')}</span>
                </Nav>

                <Nav align="stretch" className="top-right-nav">
                    <span>{userName}</span>
                    <Nav.Menu
                        noCaret
                        className="top-right-submenu"
                        placement="bottomEnd"
                        trigger="click"
                        //title={<CogIcon  size="sm"  style={{ minWidth: 20, height: 20 }} />}
                        title={<CogIcon style={{ minWidth: 20, height: 20 }} />}
                    >
                        <Nav.Item onClick={onOpenHelp}>{t('Help')}</Nav.Item>
                        <Nav.Item onClick={onOpenUserProfile}> {t('Profile')}</Nav.Item>
                        {hasRole(RoleType.Owner) && hasPermission(Capabilities.subscription_profile) && (
                            <Nav.Item onClick={onOpenUserSubscription}> {t('Subscription')}</Nav.Item>
                        )}
                        <Nav.Item onClick={onOpenSettings}>{t('Settings')}</Nav.Item>
                        <Nav.Item onClick={onSignOut}>{t('Sign out')}</Nav.Item>
                    </Nav.Menu>
                </Nav>
            </Navbar>
            {conversionsQuestion.element}
        </Header>
    )
}

// --- unsaved-changes marker ---

/**
 * The "*" behind a tab name.
 *
 * Its own component so it can listen for the flag: drawn as part of the header,
 * it only ever showed what the flag said when the header last happened to draw.
 */
const DirtyMark = ({ tabId }: { tabId: string }) => {
    useSignals()
    // Read for the subscription: a change of any tab's flag draws this again.
    void dirtyRevision.value
    return tabStore.findTab(tabId)?.store?.isDirty ? <>*</> : null
}

// --- layout ---

export default function LayoutManager() {
    useSignals()
    const { t } = useTranslation()
    const factory = useFactory()
    const [mainTabWidth, setMainTabWidth] = useState<number>(0)
    const [openProject, setOpenProject] = useState<boolean>(false)
    const navigationRef = useRef<HTMLElement | null>(null)
    const modelRef = useRef<Model>(getInitialModel())
    // the width of the open sidebar in px: what the person set, else what the
    // default weight gave it when it first opened
    const sidebarPx = useRef<number | null>(readSidebarWidth())
    // set while the sidebar is put back to its width: that change must not do it again
    const pinning = useRef(false)
    const [contextMenu, setContextMenu] = useState(initialContextMenu)
    const [pendingCloseTabId, setPendingCloseTabId] = useState<string | null>(null)

    const getEmptyLeftPosition = () => {
        if (!navigationRef.current) return 48
        const rect = navigationRef.current.getBoundingClientRect()
        return `${rect.width + mainTabWidth}px`
    }

    // sidebar and its splitter, in px
    const updateMainTabWidth = () => {
        const geometry = sidebarGeometry(modelRef.current)
        const splitter = modelRef.current.getSplitterSize() ?? 0
        setMainTabWidth((sidebarVisible.value && geometry ? geometry.width : 0) + splitter)
    }

    /** Puts the open sidebar back to its width after any other change of the layout. */
    const keepSidebarWidth = () => {
        if (pinning.current || !sidebarVisible.value) return
        const geometry = sidebarGeometry(modelRef.current)
        if (!geometry) return
        if (!sidebarPx.current) {
            sidebarPx.current = geometry.width
            return
        }
        if (Math.abs(geometry.width - sidebarPx.current) < 1) return
        pinning.current = true
        try {
            modelRef.current.doAction(
                Actions.updateNodeAttributes(ETabSet.SIDEBAR, { weight: sidebarWeightFor(geometry, sidebarPx.current) })
            )
        } finally {
            pinning.current = false
        }
    }

    const saveTabs = useCallback(() => {
        //localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(tabStore.tabs.value))
    }, [])

    // initiales load of the Tabs from localStorage
    useEffect(() => {
        const savedTabs = localStorage.getItem(STORAGE_KEY_TABS)
        if (savedTabs) {
            const parsedTabs: TTabInput[] = JSON.parse(savedTabs)
            parsedTabs.forEach(tabStore.addTab)
            //console.log('Stored tabs loaded ...')
        }
    }, [tabStore.addTab])

    // sync tabs
    useEffect(() => {
        const mainTabSet = modelRef.current.getNodeById(ETabSet.MAINTAB)

        tabStore.tabs.value.forEach((tab) => {
            const existingNode = modelRef.current.getNodeById(tab.id)
            // console.log(tab, existingNode)
            if (!existingNode && mainTabSet) {
                modelRef.current.doAction(
                    Actions.addNode(
                        {
                            type: 'tab',
                            id: tab.id,
                            name: getTabName(tab),
                            component: tab.component,
                            config: tab,
                        },
                        tab.context || ETabSet.MAINTAB,
                        DockLocation.CENTER,
                        -1
                    )
                )
            } else if (existingNode && existingNode.getType() === 'tab') {
                const tabNode = existingNode as TabNode
                const expectedTitle = getTabName(tab)
                if (tabNode.getName() !== expectedTitle) {
                    modelRef.current.doAction(Actions.renameTab(tabNode.getId(), expectedTitle))
                }
            }
        })

        updateMainTabWidth()

        // close all not existing tabs
        mainTabSet?.getChildren().forEach((node) => {
            if (!tabStore.tabs.value.some((tab) => tab.id === node.getId())) {
                modelRef.current.doAction(Actions.deleteTab(node.getId()))
            }
        })

        // store tabs and Layout
        //saveTabs()
        //saveLayout()
    }, [tabStore.tabs.value, modelRef.current])

    // Sidebar handling per Signal
    useEffect(() => {
        if (sidebarVisible.value) {
            // opened again at the width it had, the first time at the default
            const geometry = sidebarGeometry(modelRef.current)
            const weight = geometry && sidebarPx.current ? sidebarWeightFor(geometry, sidebarPx.current) : defaultSidebarWidth
            modelRef.current.doAction(Actions.updateNodeAttributes(ETabSet.SIDEBAR, { weight }))
        } else {
            // hide sidebar; its width stays in sidebarPx
            modelRef.current.doAction(Actions.updateNodeAttributes(ETabSet.SIDEBAR, { weight: 0 }))
        }
        updateMainTabWidth()
    }, [sidebarVisible.value, modelRef.current])

    // a px width stays a px width when the window changes
    useEffect(() => {
        const onResize = () => {
            keepSidebarWidth()
            updateMainTabWidth()
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {
        updateActiveContextFromModel()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    // store layout also on resize etc.
    const handleModelChange = useCallback((newModel: Model, action: Action) => {
        if (action?.type === Actions.ADJUST_WEIGHTS && action.data?.nodeId === newModel.getRootRow().getId()) {
            // a splitter of the outer row was moved: the sidebar is now as
            // wide as it is, and stays so — also over a reload
            const geometry = sidebarGeometry(newModel)
            if (geometry && sidebarVisible.value) {
                sidebarPx.current = geometry.width
                storeSidebarWidth(geometry.width)
            }
        } else {
            keepSidebarWidth()
        }
        updateActiveContextFromModel()
        updateMainTabWidth()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onContextMenu = (
        event: React.MouseEvent,
        contextMenuItems: TContextMenuItem[],
        currentElement: TContextMenuCurrentItem,
        at?: { x: number; y: number }
    ) => {
        event.preventDefault()
        event.stopPropagation()
        const { pageX, pageY } = event

        setContextMenu({
            visible: contextMenuItems.length > 0,
            x: at?.x ?? pageX,
            y: at?.y ?? pageY,
            items: contextMenuItems,
            currentElement,
        })
    }
    const handleCloseContextMenu = () => {
        setContextMenu(initialContextMenu)
    }
    const ContextMenuItems = () => {
        return (
            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                visible={contextMenu.visible}
                closeMenu={handleCloseContextMenu}
            >
                {contextMenu.items.map((item, index) =>
                    item.separator ? (
                        <div key={index} className="contextmenu-separator" />
                    ) : (
                        <ContextMenuItem
                            key={index}
                            onClick={item.onClick!}
                            checked={item.checked}
                            currentItem={{
                                id: contextMenu.currentElement?.id || '',
                                label: contextMenu.currentElement?.label || '',
                            }}
                        >
                            {item.label}
                        </ContextMenuItem>
                    )
                )}
            </ContextMenu>
        )
    }

    // --- helpers to navigate tabsets ---
    const getLayoutRow = () => modelRef.current.getNodeById('layout')
    const getChildrenOfLayoutRow = () => getLayoutRow()?.getChildren?.() ?? []
    const isTabset = (n: any) => n?.getType?.() === 'tabset'
    const isSidebar = (n: any) => n?.getId?.() === ETabSet.SIDEBAR
    const getActiveTabsetNode = () => modelRef.current.getActiveTabset?.() ?? null

    const getRightNeighborTabsetId = (fromId: string): string | null => {
        const children = getChildrenOfLayoutRow()
        const index = children.findIndex((c: any) => c?.getId?.() === fromId)
        if (index < 0) return null
        for (let i = index + 1; i < children.length; i++) {
            const n = children[i]
            if (isTabset(n) && !isSidebar(n)) return n.getId()
        }
        return null
    }

    const tabsetContainsDataId = (tabsetId: string, dataId: string): { has: boolean; tabId?: string } => {
        const tabset = modelRef.current.getNodeById(tabsetId)
        const kids = tabset?.getChildren?.() || []
        for (const k of kids) {
            const t = tabStore.findTab(k.getId())
            if (t?.dataId === dataId) return { has: true, tabId: t.id }
        }
        return { has: false }
    }
    // Remove all other tabs that share the same dataId as the source tab (keep the source)
    const removeTabCopies = (sourceTabId: string) => {
        const dataId = tabsStore.findTab(sourceTabId)!.dataId
        const all = tabStore.getTabs()
        const targets = all.filter((t) => t.dataId === dataId && t.id !== sourceTabId)

        // Use FlexLayout action only; let onAction('FlexLayout_DeleteTab') update the store & focus
        for (const t of targets) {
            modelRef.current.doAction(Actions.deleteTab(t.id))
            tabsStore.removeTab(t.id)
        }

        // Ensure the right–clicked tab is selected at the end
        modelRef.current.doAction(Actions.selectTab(sourceTabId))
    }

    // Remove all tabs except the source tab
    const removeOtherTabs = (sourceTabId: string) => {
        const all = tabStore.getTabs()
        const targets = all.filter((t) => t.id !== sourceTabId)

        for (const t of targets) {
            modelRef.current.doAction(Actions.deleteTab(t.id))
            tabsStore.removeTab(t.id)
        }

        modelRef.current.doAction(Actions.selectTab(sourceTabId))
    }

    // --- OPEN TO THE RIGHT ---
    const openTabToRight = (tab: TTabInput) => {
        const anchorContext = tabStore.getLastNonSidebarContext() || ETabSet.MAINTAB
        const activeTabsetNode = getActiveTabsetNode()
        const activeTabsetId = activeTabsetNode?.getId?.() || anchorContext
        const baseTabsetId =
            activeTabsetId === ETabSet.SIDEBAR ? tabStore.getLastNonSidebarContext() || ETabSet.MAINTAB : activeTabsetId

        const rightTabsetId = getRightNeighborTabsetId(baseTabsetId)
        if (rightTabsetId) {
            const { has, tabId } = tabsetContainsDataId(rightTabsetId, tab.dataId)
            if (has && tabId) {
                tabStore.setActiveKey(rightTabsetId, tabId)
                modelRef.current.doAction(Actions.selectTab(tabId))
                return
            }
            const newTab = tabStore.addTab({ ...tab, context: rightTabsetId })
            modelRef.current.doAction(
                Actions.addNode(
                    { type: 'tab', id: newTab.id, name: newTab.name, component: newTab.component, config: newTab },
                    rightTabsetId,
                    DockLocation.CENTER,
                    -1
                )
            )
            modelRef.current.doAction(Actions.selectTab(newTab.id))
            return
        }

        // Create a new tabset to the RIGHT and add the duplicate
        const newTab = tabStore.addTab({ ...tab })
        modelRef.current.doAction(
            Actions.addNode(
                { type: 'tab', id: newTab.id, name: newTab.name, component: newTab.component, config: newTab },
                baseTabsetId,
                DockLocation.RIGHT,
                -1
            )
        )
        const node = modelRef.current.getNodeById(newTab.id)
        const parent = node?.getParent?.()
        const newContextId = parent?.getId?.() || ETabSet.MAINTAB
        tabStore.updateTabContext(newTab.id, newContextId)
        tabStore.setActiveContext(newContextId)
        tabStore.setActiveKey(newContextId, newTab.id)
        modelRef.current.doAction(Actions.selectTab(newTab.id))
    }

    // open a new tab or select an existing one by dataId (prefer current tabset)
    const addTab = (tab: TTabInput) => {
        const all = tabStore.getTabs()
        const preferredContext =
            tab.context || tabStore.getLastNonSidebarContext() || tabStore.getActiveContext() || ETabSet.MAINTAB

        // Find any existing with same dataId
        const matches = all.filter((t) => t.dataId === tab.dataId)
        if (matches.length) {
            // Prefer the one in the current non-sidebar context
            const inPreferred = matches.find((t) => (t.context || ETabSet.MAINTAB) === preferredContext)
            const target = inPreferred || matches[matches.length - 1] // fallback: most recent
            const ctx = target.context || ETabSet.MAINTAB
            tabStore.setActiveKey(ctx, target.id)
            modelRef.current.doAction(Actions.selectTab(target.id))
            return target
        }

        // No existing → open in preferred (never the sidebar)
        const targetContext = preferredContext === ETabSet.SIDEBAR ? ETabSet.MAINTAB : preferredContext
        const newTab = tabStore.addTab({ ...tab, context: targetContext })
        modelRef.current.doAction(Actions.selectTab(newTab.id))
        return newTab
    }

    const NoProjectSelected = () => {
        return (
            <Container className="sidebar-content sidebar-content-top-space">
                <p>{t('No project selected.')}</p>
                <p>
                    <Button appearance="ghost" onClick={() => setOpenProject(true)}>
                        {t('Open project')}
                    </Button>
                </p>
            </Container>
        )
    }

    const handleTabList = (action: string, params: any) => {
        let controller = params?.tab?.controller ?? null
        if (!controller) controller = tabStore?.currentTab?.value?.controller ?? null

        let func = controller[action] ? controller[action] : null
        if (!func) func = controller[action as never]
        if (!func) return null

        /** @ts-ignore */
        return func(params)
    }

    const cmd = (action: string, data?: any, defaultValue: any = '') => {
        if (!handleTabList) return defaultValue
        return handleTabList(action, data)
    }
    const closeTab = (tabId: string) => {
        const tab = tabStore.findTab(tabId)
        if (tab?.store?.isDirty) {
            setPendingCloseTabId(tabId)
            return
        }
        closeTabNow(tabId)
    }

    const value: TLayoutContext = {
        openProject,
        setOpenProject,
        NoProjectSelected,
        model: modelRef.current,
        addTab,
        cmd,
        onContextMenu: onContextMenu,
        openTabToRight,
        closeTab,
        removeTabCopies,
        removeOtherTabs,
    }

    const onRenderTab = (node: TabNode, renderValues: ITabRenderValues) => {
        const cfg = node.getConfig() as TTabItem | undefined

        const handleHeaderContextMenu = (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            // skip sidebar tabs
            const parentId = node.getParent()?.getId?.() || ''
            if (parentId === ETabSet.SIDEBAR || !cfg) return

            const tabInput: TTabInput = {
                dataId: cfg.dataId ?? node.getId(),
                name: cfg.name,
                component: cfg.component,
                controller: cfg.controller,
                icon: cfg.icon,
                store: cfg.store ?? {},
            }

            onContextMenu(
                e,
                [
                    { label: t('Open to the Right'), onClick: () => openTabToRight(tabInput) },
                    { label: t('Remove tab copies'), onClick: () => removeTabCopies(node.getId()) },
                    { label: t('Remove other tabs'), onClick: () => removeOtherTabs(node.getId()) },
                ],
                {
                    id: tabInput.dataId,
                    label: tabInput.name,
                }
            )
        }

        const icon = cfg?.icon
        const leading = React.isValidElement(icon) ? icon : renderValues.leading
        renderValues.leading = leading ? <span onContextMenu={handleHeaderContextMenu}>{leading}</span> : undefined

        // The name shortens with "…" at --tab-max-width (App.scss); the full
        // name is the tooltip. Read on every draw, so a renamed tab and one
        // restored from the saved layout carry it too (operator, 15.10.2026).
        const originalContent = renderValues.content
        renderValues.content = (
            <span className="tab-button-label" onContextMenu={handleHeaderContextMenu}>
                <span className="tab-button-name" title={node.getName()}>
                    {originalContent}
                </span>
                <span className="tab-button-mark">
                    {' '}
                    <DirtyMark tabId={node.getId()} />
                </span>
            </span>
        )

        if (Array.isArray(renderValues.buttons) && renderValues.buttons.length) {
            renderValues.buttons = renderValues.buttons.map((btn, i) => (
                <span key={i} onContextMenu={handleHeaderContextMenu}>
                    {btn}
                </span>
            ))
        }
    }

    const getActiveTabsetId = () => {
        const active = modelRef.current.getActiveTabset?.()
        return active?.getId?.() || ETabSet.MAINTAB
    }

    const updateActiveContextFromModel = () => {
        const active = modelRef.current.getActiveTabset?.()
        const id = active?.getId?.() || ETabSet.MAINTAB
        if (id !== ETabSet.SIDEBAR) {
            tabStore.setActiveContext(id)
        }
    }
    const closeTabNow = (tabId: string) => {
        const tab = tabStore.findTab(tabId)
        const context = tab?.context

        modelRef.current.doAction(Actions.deleteTab(tabId))
        tabStore.removeTab(tabId)

        const remainingTabs = tabStore.getTabs().filter((t) => t.context === context)
        if (remainingTabs.length > 0) {
            const lastOpenedTabId = remainingTabs[remainingTabs.length - 1].id
            tabStore.setActiveKey(context!, lastOpenedTabId)
            modelRef.current.doAction(Actions.selectTab(lastOpenedTabId))
        }
    }
    const closeAllTabsNow = useCallback(() => {
        // Cancel any pending "unsaved changes" modal
        setPendingCloseTabId(null)

        // Copy list first; removing tabs mutates the signal
        const allTabs = [...tabStore.tabs.value]

        for (const t of allTabs) {
            // Force-discard dirty state when switching/closing project
            if (t?.store) t.store.isDirty = false

            // Remove from FlexLayout (safe even if node doesn't exist)
            try {
                modelRef.current.doAction(Actions.deleteTab(t.id))
            } catch {
                // ignore
            }

            // Remove from tab store
            tabStore.removeTab(t.id)
        }
    }, [])
    const prevProjectIdRef = useRef<string | null>(project.currentProject.value?._id || null)

    useSignalEffect(() => {
        const nextId = project.currentProject.value?._id || null
        const prevId = prevProjectIdRef.current

        if (prevId !== nextId) {
            prevProjectIdRef.current = nextId
            closeAllTabsNow()
        }
    })

    useEffect(() => {
        const socket = getSocket()

        const onResourceChanged = (payload: any) => {
            //console.log(payload)
            // payload: { table: string, action: 'updated' | 'deleted', ids?: string[] }
            if (!payload) return
            if (payload.action !== 'deleted') return

            const table = String(payload.table || '')
            const ids: string[] = Array.isArray(payload.ids) ? payload.ids.map(String) : []
            if (!table || ids.length === 0) return

            // Close all open tabs that reference one of the deleted ids.
            // Convention: edit tabs store resource id in tab.store.id
            //console.log('tabStore.tabs.value', tabStore.tabs.value)
            for (const tab of [...tabStore.tabs.value]) {
                const resourceId = String((tab as any)?.store?.resourceId || '')
                //console.log('tab', t.id, 'resourceId', resourceId)
                if (!resourceId) continue
                if (!ids.includes(resourceId)) continue

                const tabTable = String((tab as any)?.store?.table || '')
                if (tabTable && tabTable !== table) continue

                closeTab(tab.id)
                toastError(t('This resource has been deleted'))
            }
        }

        socket.on('resource:changed', onResourceChanged)
        return () => {
            socket.off('resource:changed', onResourceChanged)
        }
    }, [])

    return (
        <>
            <NoConnectionMessage />
            <OptimizedMessage />

            <AuthProvider>
                <LayoutContext.Provider value={value}>
                    <div className="layout-container" style={{ width: '100%', height: '100%' }}>
                        <TopNavbar />
                        <div className="layout-wrapper">
                            <LeftNavigation ref={navigationRef} />
                            <Layout
                                model={modelRef.current}
                                factory={factory}
                                onModelChange={handleModelChange}
                                onRenderTab={onRenderTab}
                                // the panes follow the splitter while it is dragged, not only
                                // on release (operator, 15.10.2026; may be switched off again)
                                realtimeResize
                                onAction={(action) => {
                                    if (action.type == 'FlexLayout_SelectTab') {
                                        tabStore.activeKey.value = action.data.tabNode
                                        const tabId = action.data.tabNode
                                        const node = modelRef.current.getNodeById(tabId)
                                        const parent = node?.getParent()
                                        const context = parent?.getId() || 'unknown'
                                        //console.log(tabId)
                                        if (context !== ETabSet.SIDEBAR) {
                                            tabStore.setActiveContext(context)
                                        }
                                    } else if (action.type === 'FlexLayout_SetActiveTabset') {
                                        // This fires when the user clicks inside a tabset’s CONTENT area
                                        // action.data.tabsetNode is the tabset id
                                        let context = action.data.tabsetNode || getActiveTabsetId()
                                        if (context !== ETabSet.SIDEBAR) {
                                            tabStore.setActiveContext(context)
                                        }
                                    } else if (action.type === 'FlexLayout_MoveNode') {
                                        const tabId = action.data.fromNode
                                        const newContext = action.data.toNode
                                        const targetId = modelRef.current.getNodeById(newContext)?.getId() || ''

                                        // REMOVE DUPLICATES
                                        const tab = tabStore.findTab(tabId)
                                        const targetChildren = modelRef.current.getNodeById(targetId)?.getChildren()

                                        targetChildren?.forEach((child) => {
                                            // Check for same data id but different tab ID. if it's the same tab ID we are
                                            // just trying to move it inside the same tabset
                                            if (
                                                tab?.dataId === tabStore.findTab(child.getId())?.dataId &&
                                                tab?.id !== tabStore.findTab(child.getId())?.id
                                            ) {
                                                //console.log('removing')
                                                tabStore.removeTab(tabId)
                                                modelRef.current.doAction(Actions.deleteTab(tabId))
                                            }
                                        })

                                        if (tabId && targetId) {
                                            tabStore.updateTabContext(tabId, targetId)
                                            tabStore.setActiveKey(targetId, tabId)
                                        }
                                    } else if (action.type === 'FlexLayout_DeleteTab') {
                                        const tabId = action.data.node
                                        const tab = tabStore.findTab(tabId)

                                        if (tab?.store?.isDirty) {
                                            setPendingCloseTabId(tabId)
                                            return undefined as any
                                        }
                                        closeTabNow(tabId)
                                    }
                                    return action
                                }}
                            />
                            {!tabStore.hasOpenTabs.value && <div className="no-open-tabs empty-tabset-bg" style={{ left: getEmptyLeftPosition() }}>
                                <ContentLogo />
                            </div>
                            }
                            {openProject && <ProjectList />}
                        </div>
                        <Modal open={Boolean(pendingCloseTabId)} onClose={() => setPendingCloseTabId(null)} backdrop="static">
                            <Modal.Header>
                                <Modal.Title>{t('Unsaved changes')}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>{t('This tab has unsaved changes. Do you want to save before closing?')}</Modal.Body>
                            <Modal.Footer>
                                <Button
                                    appearance="primary"
                                    onClick={async () => {
                                        const tabId = pendingCloseTabId!
                                        const tab = tabStore.findTab(tabId)

                                        try {
                                            await tab?.store?.save?.()
                                        } finally {
                                            // close tab after save attempt
                                            setPendingCloseTabId(null)
                                            tab?.store && (tab.store.isDirty = false)
                                            closeTabNow(tabId)
                                        }
                                    }}
                                >
                                    {t('Save & Close')}
                                </Button>

                                <Button
                                    appearance="ghost"
                                    onClick={() => {
                                        const tabId = pendingCloseTabId!
                                        const tab = tabStore.findTab(tabId)

                                        setPendingCloseTabId(null)
                                        tab?.store && (tab.store.isDirty = false)
                                        closeTabNow(tabId)
                                    }}
                                >
                                    {t('Discard')}
                                </Button>

                                <Button appearance="subtle" onClick={() => setPendingCloseTabId(null)}>
                                    {t('Cancel')}
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>
                    <ContextMenuItems />
                </LayoutContext.Provider>
            </AuthProvider>
        </>
    )
}
